import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import useFloatGrid, { getFloatGridCustomProp } from '../../features/useFloatGrid'

/**
 * Bronson FloatingButton component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-floating-button--default.html
 *
 * @param {boolean} [ariaExpanded=false] - Set the `[aria-expanded]` according to state.
 * @param {string} [controls] - Identifier for the `[aria-controls]` value.
 * @param {boolean} [disabled] - Disable the FloatingButton.
 * @param {string} [icon] - The icon identifier.
 * @param {string} [label] - The button label.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {React.ReactPortal|JSX.Element} - The FloatingButton component.
 * @constructor
 */
export function FloatingButton({ ariaExpanded = false, controls, disabled, icon, label, testId, ...otherProps }) {
  const [area, setArea] = useState(null)
  const ref = useRef(null)
  const floatGrid = useFloatGrid(area)

  const iClassNameList = classNames('c-floating-button__icon', 'c-icon', {
    [`c-icon--[${icon}] `]: icon,
  }).trim()

  useEffect(() => {
    if (!area) {
      setTimeout(() => {
        // Get custom prop from CSS.
        setArea(getFloatGridCustomProp(ref?.current))
      }, 0)
    }
  }, [ref, area])

  const comp = (
    <div className="c-floating-button" ref={ref} {...otherProps} data-testid={testId}>
      <button
        className="c-btn c-floating-button__button"
        title={label}
        aria-expanded={ariaExpanded}
        aria-controls={controls}
        disabled={disabled}
        type="button"
      >
        <i className={iClassNameList} aria-hidden="true" role="img" />
        <span className="c-floating-button__label">{label}</span>
      </button>
    </div>
  )

  return area ? createPortal(comp, floatGrid) : comp
}

FloatingButton.propTypes = {
  ariaExpanded: PropTypes.bool, // Bronson template: 'aria-expanded'.
  controls: PropTypes.string, // Bronson template: 'controls'.
  disabled: PropTypes.bool, // Bronson template: 'disabled'.
  icon: PropTypes.string, // Bronson template: 'icon'.
  label: PropTypes.string, // Bronson template: 'label'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
